import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  addDays,
  format,
  isToday,
  parseISO,
  startOfToday,
} from "date-fns";
import { pt } from "date-fns/locale";
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  CircleDot,
  GripVertical,
  ListTodo,
  PanelsTopLeft,
  Plus,
  Save,
  Sparkles,
  Target,
  TrendingUp,
  UsersRound,
  X,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { api } from "@/lib/api";
import { cn, safeUrl } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useClients, useEventCategories, useUsers, clientMap } from "@/lib/hooks";
import { PRIORITY, TASK_STATUS, eurFmt, isTaskLate } from "@/lib/constants";
import { EventModal } from "@/components/EventModal";
import { PageHeader } from "@/components/PageHeader";
import { DayCheckInCard } from "@/components/DayCheckInCard";
import { TaskModal } from "@/components/TaskModal";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const COMMON_DEFAULT_WIDGETS = ["summary", "metrics", "agenda", "priorities", "team", "clients"];
const ADMIN_DEFAULT_WIDGETS = ["summary", "metrics", "management", "agenda", "priorities", "team", "clients"];
const COMMON_WIDGETS = [...COMMON_DEFAULT_WIDGETS, "shortcuts", "notes"];
const ADMIN_WIDGETS = [...ADMIN_DEFAULT_WIDGETS, "shortcuts", "notes"];
const WIDGET_LABELS = {
  summary: "Visão do dia",
  metrics: "Indicadores principais",
  management: "Visão de gestão",
  agenda: "Próximos na agenda",
  priorities: "Prioridades",
  team: "Equipa hoje",
  clients: "Clientes em acompanhamento",
  shortcuts: "Acessos rápidos",
  notes: "Bloco de notas",
};

function StatCard({ icon: Icon, label, value, detail, tone = "primary", to }) {
  const colors = {
    primary: "bg-primary/15 text-primary",
    blue: "bg-blue-500/10 text-blue-500",
    green: "bg-emerald-500/10 text-emerald-500",
    red: "bg-red-500/10 text-red-500",
  };
  const content = (
    <Card className="group h-full border-border p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${colors[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {to && <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />}
      </div>
      <div className="mt-3 font-poppins text-2xl font-bold">{value}</div>
      <div className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xs text-muted-foreground">{detail}</div>
    </Card>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function EmptyState({ icon: Icon, text, action, onAction }) {
  return (
    <div className="flex min-h-[145px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-5 text-center">
      <Icon className="mb-2 h-6 w-6 text-muted-foreground/60" />
      <p className="text-sm text-muted-foreground">{text}</p>
      {action && <Button variant="link" size="sm" onClick={onAction} className="mt-1 text-primary">{action}</Button>}
    </div>
  );
}

function DashboardWidget({
  id,
  order,
  visible,
  customizing,
  className,
  dragId,
  onRemove,
  onDragStart,
  onDragEnd,
  children,
}) {
  const reduceMotion = useReducedMotion();
  if (!visible) return null;

  const isDragging = dragId === id;

  return (
    <div
      data-widget-id={id}
      style={{ order }}
      draggable={customizing}
      onDragStart={(event) => {
        if (!customizing) return;
        event.stopPropagation();
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", id);
        onDragStart(id);
      }}
      onDragEnd={() => onDragEnd()}
      className={cn(
        "rounded-xl",
        className,
        customizing && "cursor-grab select-none transition-opacity active:cursor-grabbing",
        isDragging && "opacity-40",
      )}
    >
      <motion.div
        layout={reduceMotion ? false : "position"}
        transition={{ type: "spring", stiffness: 560, damping: 42, mass: 0.7 }}
        className={cn(
          "relative h-full rounded-xl transition-[outline-color]",
          customizing && "outline-dashed outline-2 outline-offset-2",
          customizing && (isDragging ? "outline-primary" : "outline-primary/25"),
        )}
      >
        {customizing && (
          <div className="absolute right-2 top-2 z-20 flex items-center gap-0.5 rounded-lg border border-border bg-background/95 p-0.5 shadow-sm backdrop-blur">
            <span
              className="flex items-center rounded-md p-1.5 text-muted-foreground"
              title="Arrasta o cartão para mudar a ordem"
            >
              <GripVertical className="h-4 w-4" />
            </span>
            <button
              type="button"
              draggable={false}
              onDragStart={(event) => event.stopPropagation()}
              onClick={() => onRemove(id)}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              aria-label={`Remover ${WIDGET_LABELS[id]}`}
              title="Remover da Dashboard"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {children}
      </motion.div>
    </div>
  );
}

function AdminManagementOverview({ metrics }) {
  const openProposals = Math.max(
    0,
    metrics.funnel.sentCount - metrics.funnel.wonCount - metrics.funnel.lostCount,
  );
  const workload = [...metrics.workload]
    .sort((first, second) => (second.tasks + second.hours) - (first.tasks + first.hours))
    .slice(0, 6);
  const maxWorkload = Math.max(1, ...workload.map((member) => member.tasks + member.hours));

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-poppins text-lg font-semibold">Visão de gestão</h2>
          <p className="text-xs text-muted-foreground">Operação, equipa e negócio num só ponto</p>
        </div>
        <Link to="/hub" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          Abrir Hub Executivo <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="border-border p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary"><Target className="h-4 w-4" /></div>
            <div>
              <h3 className="font-poppins text-sm font-semibold">Pipeline comercial</h3>
              <p className="text-[11px] text-muted-foreground">Estado das propostas</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-muted/40 p-2.5 text-center"><div className="font-poppins text-lg font-bold">{openProposals}</div><div className="text-[10px] text-muted-foreground">Em aberto</div></div>
            <div className="rounded-lg bg-emerald-500/10 p-2.5 text-center"><div className="font-poppins text-lg font-bold text-emerald-600">{metrics.funnel.wonCount}</div><div className="text-[10px] text-muted-foreground">Ganhas</div></div>
            <div className="rounded-lg bg-red-500/10 p-2.5 text-center"><div className="font-poppins text-lg font-bold text-red-500">{metrics.funnel.lostCount}</div><div className="text-[10px] text-muted-foreground">Perdidas</div></div>
          </div>
          <div className="mt-3 flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <span className="text-xs text-muted-foreground">Valor em propostas</span>
            <span className="font-poppins text-sm font-semibold">{eurFmt.format(metrics.funnel.sentValue)}</span>
          </div>
        </Card>

        <Card className="border-border p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${metrics.alerts.length ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-500"}`}><AlertCircle className="h-4 w-4" /></div>
            <div>
              <h3 className="font-poppins text-sm font-semibold">Saúde da operação</h3>
              <p className="text-[11px] text-muted-foreground">Entregáveis fora do prazo</p>
            </div>
          </div>
          {metrics.alerts.length === 0 ? (
            <div className="flex min-h-[118px] flex-col items-center justify-center rounded-xl bg-emerald-500/5 text-center">
              <CheckCircle2 className="mb-2 h-6 w-6 text-emerald-500" />
              <div className="text-sm font-medium">Operação saudável</div>
              <div className="text-xs text-muted-foreground">Sem entregáveis em atraso</div>
            </div>
          ) : (
            <div className="space-y-2">
              {metrics.alerts.slice(0, 4).map((alert) => (
                <div key={alert.id} className="flex items-center gap-2 rounded-lg bg-red-500/5 px-3 py-2">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
                  <span className="min-w-0 flex-1 truncate text-xs font-medium">{alert.title}</span>
                  <span className="text-[10px] text-red-500">{alert.dueDate}</span>
                </div>
              ))}
              {metrics.alerts.length > 4 && <div className="text-center text-[11px] text-muted-foreground">Mais {metrics.alerts.length - 4} alertas</div>}
            </div>
          )}
        </Card>

        <Card className="border-border p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500"><UsersRound className="h-4 w-4" /></div>
            <div>
              <h3 className="font-poppins text-sm font-semibold">Carga da equipa</h3>
              <p className="text-[11px] text-muted-foreground">Horas e tarefas ativas</p>
            </div>
          </div>
          {workload.length === 0 ? <p className="text-sm text-muted-foreground">Sem dados de carga.</p> : (
            <div className="space-y-3">
              {workload.map((member) => {
                const total = member.tasks + member.hours;
                return (
                  <div key={member.name}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                      <span className="truncate font-medium">{member.name}</span>
                      <span className="shrink-0 text-[10px] text-muted-foreground">{member.hours}h • {member.tasks} tarefas</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(4, (total / maxWorkload) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [taskOpen, setTaskOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [dragId, setDragId] = useState(null);
  const gridRef = useRef(null);
  const orderRef = useRef([]);
  const dragSnapshot = useRef(null);
  const didDropRef = useRef(false);
  const defaultWidgets = useMemo(
    () => (user.role === "ADMIN" ? ADMIN_DEFAULT_WIDGETS : COMMON_DEFAULT_WIDGETS),
    [user.role],
  );
  const widgetCatalog = useMemo(
    () => (user.role === "ADMIN" ? ADMIN_WIDGETS : COMMON_WIDGETS),
    [user.role],
  );
  const [widgetOrder, setWidgetOrder] = useState(defaultWidgets);
  const [scratch, setScratch] = useState("");
  const [scratchSaved, setScratchSaved] = useState(true);
  const today = format(new Date(), "yyyy-MM-dd");
  const nextWeek = format(addDays(new Date(), 7), "yyyy-MM-dd");
  const { data: users = [] } = useUsers();
  const { data: clients = [] } = useClients();
  const { data: categories = [] } = useEventCategories();
  const clientsById = useMemo(() => clientMap(clients), [clients]);
  const categoriesById = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category])),
    [categories],
  );

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ["tasks", "mine"],
    queryFn: async () => (await api.get("/tasks", { params: { mine: true } })).data,
  });
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ["events", "dashboard", today, nextWeek],
    queryFn: async () => (
      await api.get("/events", { params: { start: today, end: nextWeek } })
    ).data,
  });
  const { data: executive } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    enabled: user.role === "ADMIN",
  });
  const { data: preferences } = useQuery({
    queryKey: ["dashboard-preferences", user.id],
    queryFn: async () => (await api.get("/dashboard-preferences")).data,
  });
  const { data: scratchData } = useQuery({
    queryKey: ["scratchpad"],
    queryFn: async () => (await api.get("/scratchpad")).data,
    enabled: widgetOrder.includes("notes"),
  });

  useEffect(() => {
    if (!preferences) return;
    if (!preferences.configured) {
      setWidgetOrder(defaultWidgets);
      return;
    }
    setWidgetOrder(
      (preferences.widgets || []).filter((widgetId) => widgetCatalog.includes(widgetId)),
    );
  }, [preferences, defaultWidgets, widgetCatalog]);

  useEffect(() => {
    if (scratchData) setScratch(scratchData.content || "");
  }, [scratchData]);

  const openTasks = tasks.filter((task) => task.status !== "CONCLUIDO");
  const overdueTasks = openTasks.filter((task) => isTaskLate(task, today));
  const todayTasks = openTasks.filter((task) => task.dueDate === today);
  const priorityTasks = [...openTasks]
    .sort((first, second) => {
      const priorityOrder = { URGENTE: 0, ALTA: 1, MEDIA: 2, BAIXA: 3 };
      const overdueDifference = Number(isTaskLate(second, today)) - Number(isTaskLate(first, today));
      if (overdueDifference) return overdueDifference;
      const priorityDifference = (priorityOrder[first.priority] ?? 4) - (priorityOrder[second.priority] ?? 4);
      if (priorityDifference) return priorityDifference;
      return (first.dueDate || "9999").localeCompare(second.dueDate || "9999");
    })
    .slice(0, 5);

  const myEvents = events
    .filter((event) => (event.assigneeIds || []).includes(user.id))
    .sort((first, second) => `${first.date}${first.startTime}`.localeCompare(`${second.date}${second.startTime}`));
  const myEventsToday = myEvents.filter((event) => event.date === today);
  const nextEvents = myEvents.slice(0, 5);
  const teamEventsToday = events.filter((event) => event.date === today);
  const scheduledUserIds = new Set(teamEventsToday.flatMap((event) => event.assigneeIds || []));
  const activeClients = clients.filter((client) => client.status === "ATIVO");

  const completeTask = async (taskId) => {
    await api.patch(`/tasks/${taskId}/status`, { status: "CONCLUIDO" });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };

  orderRef.current = widgetOrder;

  const persistWidgetOrder = (nextOrder) => {
    api.put("/dashboard-preferences", { widgets: nextOrder }).catch(() => {
      toast.error("Não foi possível guardar a organização da Dashboard");
    });
  };

  const saveWidgetOrder = (nextOrder) => {
    setWidgetOrder(nextOrder);
    persistWidgetOrder(nextOrder);
  };

  const startWidgetDrag = (id) => {
    didDropRef.current = false;
    dragSnapshot.current = widgetOrder;
    setDragId(id);
  };

  const endWidgetDrag = () => {
    if (didDropRef.current) {
      persistWidgetOrder(orderRef.current);
    } else if (dragSnapshot.current) {
      setWidgetOrder(dragSnapshot.current);
    }
    dragSnapshot.current = null;
    didDropRef.current = false;
    setDragId(null);
  };

  const dropIndexFromPoint = (clientX, clientY) => {
    const container = gridRef.current;
    if (!container) return -1;
    const nodes = Array.from(container.querySelectorAll("[data-widget-id]"));
    let best = null;
    for (const node of nodes) {
      const rect = node.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.hypot(clientX - centerX, clientY - centerY);
      if (!best || distance < best.distance) {
        const after =
          clientY > rect.bottom ? true : clientY < rect.top ? false : clientX > centerX;
        best = { id: node.dataset.widgetId, after, distance };
      }
    }
    if (!best) return -1;
    return orderRef.current.indexOf(best.id) + (best.after ? 1 : 0);
  };

  const handleGridDragOver = (event) => {
    if (!customizing || !dragId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const index = dropIndexFromPoint(event.clientX, event.clientY);
    if (index < 0) return;
    setWidgetOrder((current) => {
      const from = current.indexOf(dragId);
      if (from < 0) return current;
      let target = index > from ? index - 1 : index;
      target = Math.max(0, Math.min(current.length - 1, target));
      if (target === from) return current;
      const next = [...current];
      next.splice(from, 1);
      next.splice(target, 0, dragId);
      return next;
    });
  };

  const handleGridDrop = (event) => {
    if (!customizing || !dragId) return;
    event.preventDefault();
    didDropRef.current = true;
  };

  const widgetDnd = {
    customizing,
    dragId,
    onRemove: (id) => saveWidgetOrder(widgetOrder.filter((widgetId) => widgetId !== id)),
    onDragStart: startWidgetDrag,
    onDragEnd: endWidgetDrag,
  };

  const saveScratch = async () => {
    try {
      await api.put("/scratchpad", { content: scratch });
      setScratchSaved(true);
      queryClient.invalidateQueries({ queryKey: ["scratchpad"] });
      toast.success("Notas guardadas");
    } catch {
      toast.error("Não foi possível guardar as notas");
    }
  };

  const hiddenWidgets = widgetCatalog.filter((widgetId) => !widgetOrder.includes(widgetId));

  const formatEventDate = (event) => {
    if (isToday(parseISO(event.date))) return "Hoje";
    return format(parseISO(event.date), "EEE, d MMM", { locale: pt });
  };

  const firstName = user.name.split(" ")[0];

  return (
    <div>
      <PageHeader
        title={`Olá, ${firstName} 👋`}
        subtitle={format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: pt })}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant={customizing ? "secondary" : "outline"}
              onClick={() => setCustomizing((current) => !current)}
            >
              <Plus className="mr-1.5 h-4 w-4" /> Personalizar
            </Button>
            <Button variant="outline" onClick={() => setTaskOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Nova tarefa
            </Button>
            <Button onClick={() => setEventOpen(true)} className="bg-primary text-black hover:bg-primary/90">
              <CalendarDays className="mr-1.5 h-4 w-4" /> Agendar
            </Button>
          </div>
        }
      />

      <DayCheckInCard />

      {customizing && (
        <Card className="mb-6 border-primary/30 bg-primary/5 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex items-center gap-2 lg:min-w-[280px]">
              <GripVertical className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm font-semibold">Organizar Dashboard</div>
                <div className="text-xs text-muted-foreground">Arrasta qualquer cartão para mudar a ordem.</div>
              </div>
            </div>
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {hiddenWidgets.length === 0 ? (
                <span className="text-xs text-muted-foreground">Todos os blocos estão visíveis.</span>
              ) : (
                hiddenWidgets.map((widgetId) => (
                  <Button
                    key={widgetId}
                    size="sm"
                    variant="outline"
                    onClick={() => saveWidgetOrder([...widgetOrder, widgetId])}
                    className="h-8 bg-background"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> {WIDGET_LABELS[widgetId]}
                  </Button>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => saveWidgetOrder(defaultWidgets)}>Repor padrão</Button>
              <Button size="sm" onClick={() => setCustomizing(false)} className="bg-primary text-black hover:bg-primary/90">Concluído</Button>
            </div>
          </div>
        </Card>
      )}

      <div
        ref={gridRef}
        className="grid grid-cols-12 gap-5"
        onDragOver={handleGridDragOver}
        onDrop={handleGridDrop}
      >
      <DashboardWidget
        id="summary"
        order={widgetOrder.indexOf("summary")}
        visible={widgetOrder.includes("summary")}
        {...widgetDnd}
        className="col-span-12"
      >
      <Card className="relative overflow-hidden border-0 bg-[#212529] px-5 py-5 text-white shadow-sm lg:px-7">
        <div className="relative z-10 flex flex-col justify-between gap-5 md:flex-row md:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              <Sparkles className="h-4 w-4" /> Visão do dia
            </div>
            <h2 className="font-poppins text-xl font-semibold">
              {overdueTasks.length
                ? `Tens ${overdueTasks.length} ${overdueTasks.length === 1 ? "tarefa em atraso" : "tarefas em atraso"}.`
                : todayTasks.length
                  ? `Tens ${todayTasks.length} ${todayTasks.length === 1 ? "prioridade" : "prioridades"} para concluir hoje.`
                  : "O teu dia está organizado."}
            </h2>
            <p className="mt-1 text-sm text-white/60">
              {myEventsToday.length
                ? `${myEventsToday.length} ${myEventsToday.length === 1 ? "evento agendado" : "eventos agendados"} para hoje.`
                : "Sem eventos na tua agenda para hoje."}
            </p>
          </div>
          <Button asChild className="border border-white/15 bg-white/10 text-white hover:bg-white/15">
            <Link to="/workspace">
              Abrir as minhas tarefas <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <div className="absolute -right-12 -top-20 h-60 w-60 rounded-full bg-primary/20 blur-3xl" />
      </Card>
      </DashboardWidget>

      <DashboardWidget
        id="metrics"
        order={widgetOrder.indexOf("metrics")}
        visible={widgetOrder.includes("metrics")}
        {...widgetDnd}
        className="col-span-12"
      >
        {user.role === "ADMIN" ? (
          executive ? (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard icon={TrendingUp} label="MRR" value={eurFmt.format(executive.mrr)} detail="Faturação recorrente mensal" />
              <StatCard icon={BriefcaseBusiness} label="Clientes ativos" value={executive.activeClients} detail={`${executive.totalClients} clientes no total`} tone="green" to="/clientes" />
              <StatCard icon={Target} label="Taxa de conversão" value={`${executive.funnel.conversion}%`} detail={`${executive.funnel.wonCount} propostas ganhas`} tone="blue" to="/hub" />
              <StatCard icon={AlertCircle} label="Alertas operacionais" value={executive.alerts.length} detail={executive.alerts.length ? "Entregáveis em atraso" : "Operação dentro do prazo"} tone={executive.alerts.length ? "red" : "green"} to="/hub" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard icon={TrendingUp} label="MRR" value="..." detail="A carregar dados de gestão" />
              <StatCard icon={BriefcaseBusiness} label="Clientes ativos" value="..." detail="A carregar dados de gestão" tone="green" />
              <StatCard icon={Target} label="Taxa de conversão" value="..." detail="A carregar dados de gestão" tone="blue" />
              <StatCard icon={AlertCircle} label="Alertas operacionais" value="..." detail="A carregar dados de gestão" />
            </div>
          )
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard icon={ListTodo} label="Tarefas abertas" value={tasksLoading ? "..." : openTasks.length} detail={`${todayTasks.length} com prazo hoje`} to="/workspace" />
            <StatCard icon={AlertCircle} label="Em atraso" value={tasksLoading ? "..." : overdueTasks.length} detail={overdueTasks.length ? "Precisam de atenção" : "Tudo dentro do prazo"} tone={overdueTasks.length ? "red" : "green"} to="/workspace" />
            <StatCard icon={CalendarDays} label="Próximos 7 dias" value={eventsLoading ? "..." : myEvents.length} detail={`${myEventsToday.length} na agenda de hoje`} tone="blue" to="/agenda" />
            <StatCard icon={BriefcaseBusiness} label="Clientes ativos" value={activeClients.length} detail={`${clients.length} clientes registados`} tone="green" to="/clientes" />
          </div>
        )}
      </DashboardWidget>

      {user.role === "ADMIN" && executive && (
        <DashboardWidget
          id="management"
          order={widgetOrder.indexOf("management")}
          visible={widgetOrder.includes("management")}
          {...widgetDnd}
          className="col-span-12"
        >
          <AdminManagementOverview metrics={executive} />
        </DashboardWidget>
      )}

      <DashboardWidget
        id="agenda"
        order={widgetOrder.indexOf("agenda")}
        visible={widgetOrder.includes("agenda")}
        {...widgetDnd}
        className="col-span-12 xl:col-span-7"
      >
        <Card className="h-full border-border p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-poppins font-semibold">Próximos na agenda</h3>
              <p className="text-xs text-muted-foreground">Os teus próximos compromissos</p>
            </div>
            <Link to="/agenda" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">Ver agenda <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          {nextEvents.length === 0 ? (
            <EmptyState icon={CalendarDays} text="Não tens eventos nos próximos sete dias." action="Agendar evento" onAction={() => setEventOpen(true)} />
          ) : (
            <div className="space-y-2">
              {nextEvents.map((event) => {
                const category = categoriesById[event.categoryId];
                const clientName = clientsById[event.clientId]?.name;
                return (
                  <div key={event.id} className="flex items-center gap-3 rounded-xl border border-border px-3 py-3 transition-colors hover:bg-muted/30">
                    <span className="h-10 w-1 shrink-0 rounded-full" style={{ backgroundColor: category?.color || event.color || "#A886CD" }} />
                    <div className="w-16 shrink-0">
                      <div className="text-xs font-semibold capitalize">{formatEventDate(event)}</div>
                      <div className="text-[11px] text-muted-foreground">{event.allDay ? "Todo o dia" : event.startTime}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{clientName ? `${clientName} › ${event.title}` : event.title}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{category?.name || event.categoryName || "Evento"}</span>
                        {(event.assigneeIds || []).length > 1 && <span>• {(event.assigneeIds || []).length} pessoas</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </DashboardWidget>

      <DashboardWidget
        id="priorities"
        order={widgetOrder.indexOf("priorities")}
        visible={widgetOrder.includes("priorities")}
        {...widgetDnd}
        className="col-span-12 xl:col-span-5"
      >
        <Card className="h-full border-border p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-poppins font-semibold">Prioridades</h3>
              <p className="text-xs text-muted-foreground">O que precisa da tua atenção</p>
            </div>
            <Link to="/workspace" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">Ver tarefas <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          {priorityTasks.length === 0 ? (
            <EmptyState icon={CheckCircle2} text="Não tens tarefas pendentes." action="Criar tarefa" onAction={() => setTaskOpen(true)} />
          ) : (
            <div className="space-y-2">
              {priorityTasks.map((task) => {
                const overdue = isTaskLate(task, today);
                return (
                  <div key={task.id} className="group flex items-center gap-3 rounded-xl border border-border px-3 py-3 transition-colors hover:bg-muted/30">
                    <button onClick={() => completeTask(task.id)} className="text-muted-foreground transition-colors hover:text-emerald-500" aria-label="Concluir tarefa">
                      <CheckCircle2 className="h-5 w-5" />
                    </button>
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PRIORITY[task.priority]?.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{task.title}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        {clientsById[task.clientId] && <span>{clientsById[task.clientId].name}</span>}
                        {task.dueDate && <span className={overdue ? "font-medium text-destructive" : ""}>{overdue ? "Em atraso" : format(parseISO(task.dueDate), "d MMM", { locale: pt })}</span>}
                      </div>
                    </div>
                    <Badge variant="secondary" className="rounded-full text-[10px]">{TASK_STATUS[task.status]?.label}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </DashboardWidget>

      <DashboardWidget
        id="team"
        order={widgetOrder.indexOf("team")}
        visible={widgetOrder.includes("team")}
        {...widgetDnd}
        className="col-span-12 lg:col-span-7"
      >
        <Card className="h-full border-border p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-poppins font-semibold">Equipa hoje</h3>
              <p className="text-xs text-muted-foreground">Quem tem trabalho agendado</p>
            </div>
            <Link to="/agenda" className="text-xs font-medium text-primary hover:underline">Ver equipa</Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {users.map((member) => {
              const memberEvents = teamEventsToday.filter((event) => (event.assigneeIds || []).includes(member.id));
              return (
                <div key={member.id} className={`flex min-w-[190px] flex-1 items-center gap-2.5 rounded-xl border p-2.5 ${scheduledUserIds.has(member.id) ? "border-border bg-card" : "border-border/60 bg-muted/20 opacity-65"}`}>
                  <UserAvatar user={member} size="h-8 w-8" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">{member.name}</div>
                    <div className="text-[10px] text-muted-foreground">{memberEvents.length ? `${memberEvents.length} ${memberEvents.length === 1 ? "evento" : "eventos"}` : "Sem agenda"}</div>
                  </div>
                  <CircleDot className={`h-3 w-3 ${memberEvents.length ? "text-emerald-500" : "text-muted-foreground/40"}`} />
                </div>
              );
            })}
          </div>
        </Card>
      </DashboardWidget>

      <DashboardWidget
        id="clients"
        order={widgetOrder.indexOf("clients")}
        visible={widgetOrder.includes("clients")}
        {...widgetDnd}
        className="col-span-12 lg:col-span-5"
      >
        <Card className="h-full border-border p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-poppins font-semibold">Clientes em acompanhamento</h3>
              <p className="text-xs text-muted-foreground">Acesso rápido às contas ativas</p>
            </div>
            <Link to="/clientes" className="text-xs font-medium text-primary hover:underline">Ver todos</Link>
          </div>
          {activeClients.length === 0 ? (
            <EmptyState icon={BriefcaseBusiness} text="Ainda não existem clientes ativos." />
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {activeClients.slice(0, 6).map((client) => (
                <Link key={client.id} to={`/clientes/${client.id}`} className="flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary/40 hover:bg-muted/20">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/15 font-poppins text-xs font-bold text-primary">
                    {safeUrl(client.logoUrl) ? <img src={safeUrl(client.logoUrl)} alt="" className="h-full w-full object-cover" /> : client.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{client.name}</div>
                    <div className="truncate text-[10px] text-muted-foreground">{(client.services || []).slice(0, 2).join(" • ") || "Sem serviços definidos"}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </DashboardWidget>

      <DashboardWidget
        id="shortcuts"
        order={widgetOrder.indexOf("shortcuts")}
        visible={widgetOrder.includes("shortcuts")}
        {...widgetDnd}
        className="col-span-12 lg:col-span-6"
      >
        <Card className="h-full border-border p-5">
          <div className="mb-4">
            <h3 className="font-poppins font-semibold">Acessos rápidos</h3>
            <p className="text-xs text-muted-foreground">Atalhos para as áreas mais utilizadas</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/workspace" className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm font-medium transition-colors hover:border-primary/40 hover:bg-primary/5"><PanelsTopLeft className="h-4 w-4 text-primary" /> As minhas tarefas</Link>
            <Link to="/agenda" className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm font-medium transition-colors hover:border-primary/40 hover:bg-primary/5"><CalendarDays className="h-4 w-4 text-primary" /> Agenda</Link>
            <Link to="/clientes" className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm font-medium transition-colors hover:border-primary/40 hover:bg-primary/5"><BriefcaseBusiness className="h-4 w-4 text-primary" /> Clientes</Link>
            <Link to="/sops" className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm font-medium transition-colors hover:border-primary/40 hover:bg-primary/5"><BookOpen className="h-4 w-4 text-primary" /> SOPs</Link>
            {user.role === "ADMIN" && <Link to="/hub" className="col-span-2 flex items-center gap-2 rounded-xl border border-border p-3 text-sm font-medium transition-colors hover:border-primary/40 hover:bg-primary/5"><Target className="h-4 w-4 text-primary" /> Hub Executivo</Link>}
          </div>
        </Card>
      </DashboardWidget>

      <DashboardWidget
        id="notes"
        order={widgetOrder.indexOf("notes")}
        visible={widgetOrder.includes("notes")}
        {...widgetDnd}
        className="col-span-12 lg:col-span-6"
      >
        <Card className="h-full border-border p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h3 className="font-poppins font-semibold">Bloco de notas</h3>
              <p className="text-xs text-muted-foreground">Notas privadas, sincronizadas com as tuas tarefas</p>
            </div>
            <Button size="sm" variant="ghost" onClick={saveScratch} disabled={scratchSaved}>
              <Save className="mr-1.5 h-4 w-4" /> Guardar
            </Button>
          </div>
          <Textarea
            value={scratch}
            onChange={(event) => {
              setScratch(event.target.value);
              setScratchSaved(false);
            }}
            placeholder="Escreve aqui as tuas notas privadas..."
            className="min-h-[145px] resize-none"
          />
        </Card>
      </DashboardWidget>
      </div>

      <TaskModal open={taskOpen} onOpenChange={setTaskOpen} />
      <EventModal
        open={eventOpen}
        onOpenChange={setEventOpen}
        defaultDate={format(startOfToday(), "yyyy-MM-dd")}
        defaultUserId={user.id}
      />
    </div>
  );
}
